
import React, { useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    NSTable,
    NSCellInput,
    NSSelect,
} from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    OPERATIONS_EXPENSES_WS_HANDLERS, OperationExpenseType, CASH_FLOW_TOOLTIP_MESSAGE, WattsType,
} from 'views/ProFormaTable/constants';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { IncomeCalculationType } from 'constants/proForma';
import { isAllApartmentModels, isStudentHousingModel, isModelWithLeasingCostReserves } from 'helpers/proForma';
import { CapitalExpenditure } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import {
    ColumnKeys, COLUMN_KEYS_BY_MODEL_ID, CAPITAL_RESERVES_OPTIONS, CAPITAL_RESERVES_OPTION_VALUES,
} from '../constants';
import LeasingCostReservesModal from '../LeasingCostReservesModal/LeasingCostReservesModal';
import './CapitalExpendituresTable.scss';

export interface ICapitalExpendituresTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const CapitalExpendituresTable = ({ operationBeginIndex, baseCashFlowColumns }: ICapitalExpendituresTableProps) => {
    const {
        expenses, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma, operatingInfo, wattsType,
    } = useProFormaSocketContext();

    const [isLeasingCostReservesModalOpen, setIsLeasingCostReservesModalOpen] = useState(false);
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const showLeasingCostReserves = isModelWithLeasingCostReserves(proForma.proFormaModelId);

    let areaType: string;

    if (showLeasingCostReserves) {
        areaType = 'GSF';
    } else if (isAllApartmentModels(proForma.proFormaModelId) || isStudentHousingModel(proForma.proFormaModelId) || isIncomeCalculationTypeEstimate) {
        areaType = 'SF';
    } else {
        areaType = 'RSF';
    }

    const handleUpdateCapitalExpenditure = (id: number, field: string) => handleUpdateOperationsRow(
        OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.UPDATE, id, field,
    );

    const handleDeleteCapitalExpenditure = (id: number) => handleDeleteOperationsRow(
        OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.DELETE, id,
    );

    const cashFlowColumns: ColumnDef<CapitalExpenditure>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `capex-period-${column.id}`,
        accessorFn: (row, rowIndex) => (
            typeof operationsCashFlow?.[operationBeginIndex]?.capitalExpenditures?.[rowIndex]?.value === 'number'
                ? (operationsCashFlow?.[cashFlowIndex]?.capitalExpenditures?.[rowIndex]?.value || 0)
                : null
        ),
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                <span className="text-dark">
                    {formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalCapitalExpenditures || 0, showCents, 'currency')}
                </span>
            </RenderIf>
        ),
    }));

    const COLUMNS_BY_KEY: { [key in ColumnKeys]: ColumnDef<CapitalExpenditure> } = {
        [ColumnKeys.ANNUAL_GROWTH]: {
            accessorKey: 'annualGrowth',
            id: 'annualGrowth',
            header: () => <span>Annual growth</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'annualGrowth');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalCapitalExpenditures?.totalAnnualGrowth || 0, true, 'percentage')}</span>,
        },
        [ColumnKeys.PERCENT_OF_EGR]: {
            accessorKey: 'percentOfEffectiveGrossRevenue',
            id: 'percentOfEffectiveGrossRevenue',
            header: () => <span>% of effective gross revenue</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'percentOfEffectiveGrossRevenue');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
            footer: () => (
                <span>
                    {formatDisplayValue(expenses.totalCapitalExpenditures?.totalPercentOfEffectiveGrossRevenue || 0, true, 'percentage')}
                </span>
            ),
        },
        [ColumnKeys.COST_PER_UNIT_PER_YEAR]: {
            accessorKey: 'costPerUnitPerYear',
            id: 'costPerUnitPerYear',
            header: () => <span>$/unit/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'costPerUnitPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalCapitalExpenditures?.totalCostPerUnitPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_BED_PER_YEAR]: {
            accessorKey: 'valuePerBedPerYear',
            id: 'valuePerBedPerYear',
            header: () => <span>$/bed/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'valuePerBedPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalCapitalExpenditures?.totalValuePerBedPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_SF_PER_YEAR]: {
            accessorKey: 'valuePerSfPerYear',
            id: 'valuePerSfPerYear',
            header: () => <span>{`$/${areaType}/year`}</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'valuePerSfPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalCapitalExpenditures?.totalValuePerSfPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_RACK_PER_YEAR]: {
            accessorKey: 'valuePerRackPerYear',
            id: 'valuePerRackPerYear',
            header: () => <span>$/rack/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'valuePerRackPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalCapitalExpenditures?.totalValuePerRackPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_KW_PER_YEAR]: wattsType === WattsType.MEGAWATTS ? {
            accessorKey: 'valuePerMwPerYear',
            id: 'valuePerMwPerYear',
            header: () => <span>$/MW/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'valuePerMwPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalCapitalExpenditures?.totalValuePerMwPerYear || 0, true, 'currency')}</span>,
        } : {
            accessorKey: 'valuePerKwPerYear',
            id: 'valuePerKwPerYear',
            header: () => <span>$/kW/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'valuePerKwPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalCapitalExpenditures?.totalValuePerKwPerYear || 0, true, 'currency')}</span>,
        },
    };

    const capitalExpendituresColumns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <span>Capital expenditures</span>
                    <FontAwesomeIcon
                        id="plus-icon"
                        icon={faPlus}
                        className="ml-2 text-muted cursor--pointer"
                        onClick={() => handleCreateOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.CREATE)}
                    />
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <div className={classNames('w-100', { 'ml-n1': row.original.operationExpenseType !== OperationExpenseType.CAPITAL_RESERVES })}>
                        <div className="w-100">
                            <NSCellInput
                                type="text"
                                value={getValue()}
                                handleUpdateValue={updatedValue => {
                                    const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'name');
                                    if (typeof updateFn === 'function') {
                                        updateFn({
                                            target: { value: updatedValue },
                                        } as React.ChangeEvent<HTMLInputElement>);
                                    }
                                }}
                                placeholder="Enter name"
                                disabled={row.original.operationExpenseType !== OperationExpenseType.STANDARD}
                                labelClassName={row.original.operationExpenseType !== OperationExpenseType.STANDARD ? 'text-dark' : ''}
                            />
                        </div>
                        <RenderIf isTrue={row.original.operationExpenseType === OperationExpenseType.CAPITAL_RESERVES}>
                            <div className="pb-1">
                                <NSSelect
                                    name={`capex-select-${row.id}`}
                                    options={CAPITAL_RESERVES_OPTIONS}
                                    value={CAPITAL_RESERVES_OPTIONS.find(option => option.value === CAPITAL_RESERVES_OPTION_VALUES.CAPEX)}
                                    onChange={option => {
                                        if (option && option?.value === CAPITAL_RESERVES_OPTION_VALUES.OPEX) {
                                            handleDeleteCapitalExpenditure(Number(row.original.id));
                                        }
                                    }}
                                    selectedItemPrefix="From: "
                                    isClearable={false}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...customStyles,
                                        control: (provided: any) => ({
                                            ...provided,
                                            backgroundColor: '#4F5B69',
                                            marginTop: '4px',
                                            border: 'none',
                                            minHeight: '28px',
                                        }),
                                    }}
                                />
                            </div>
                        </RenderIf>
                    </div>
                    <RenderIf isTrue={showLeasingCostReserves && row.original.operationExpenseType === OperationExpenseType.LEASING_COST_RESERVE}>
                        <span
                            className="text-primary cursor--pointer mr-2 text-nowrap"
                            onClick={() => setIsLeasingCostReservesModalOpen(true)}
                            role="presentation"
                        >
                            <span>Calculate</span>
                            <FontAwesomeIcon
                                icon={faChevronRight}
                                className="ml-1"
                                size="xs"
                            />
                        </span>
                    </RenderIf>
                    <RenderIf isTrue={row.original.operationExpenseType === OperationExpenseType.STANDARD}>
                        <FontAwesomeIcon
                            id="trash-icon"
                            icon={faTrash}
                            className="ml-1 text-dark cursor--pointer"
                            onClick={() => handleDeleteCapitalExpenditure(Number(row.original.id))}
                        />
                    </RenderIf>
                </div>
            ),
            footer: () => <span className="px-1">Total capital expenditures</span>,
        },
        ...(COLUMN_KEYS_BY_MODEL_ID[proForma.proFormaModelId] || []).map(key => COLUMNS_BY_KEY[key]),
        {
            accessorKey: 'totalAmountPerYear',
            id: 'totalAmountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => (
                <div className="pr-1">
                    <NSCellInput
                        showCents={showCents}
                        type="number"
                        numberType="currency"
                        value={getValue()}
                        handleUpdateValue={updatedValue => {
                            const updateFn = handleUpdateCapitalExpenditure(Number(row.original.id), 'totalAmountPerYear');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value: updatedValue },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isRightAligned
                        placeholder="Enter amount"
                    />
                </div>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(expenses.totalCapitalExpenditures?.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...cashFlowColumns,
    ];

    const table = useReactTable({
        data: expenses.capitalExpenditures || [],
        columns: capitalExpendituresColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <NSTable className={`NSTable CapitalExpendituresTable--${proForma.proFormaModelName?.replace(/ /g, '-')} mb-1`}>
                <thead className="NSTable__thead">
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id} className="NSTable__thead__tr">
                            {headerGroup.headers.map(header => (
                                <th key={header.id} className="NSTable__thead__tr__th p-1">
                                    {header.isPlaceholder ? null : (
                                        <div>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody
                    className={classNames('NSTable__tbody', {
                        'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                    })}
                >
                    <RenderIf isTrue={table.getRowModel().rows.length}>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </RenderIf>
                    <RenderIf isTrue={!table.getRowModel().rows.length}>
                        <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            <td colSpan={5} className="NSTable__tbody__tr__td text-center text-muted py-4">
                                <div className="text-dark">Your added capital expenditures will appear here.</div>
                                <div className="small">
                                    Click the
                                    {' '}
                                    <span className="font-weight-bold text-dark">[+]</span>
                                    {' '}
                                    symbol in the
                                    {' '}
                                    <span className="font-weight-bold text-dark">Capital expenditures</span>
                                    {' '}
                                    header to add your first capital expenditure.
                                </div>
                            </td>
                            {baseCashFlowColumns.map((column, index) => (
                                <td
                                    key={`capex-period-${column.id}`}
                                    className="NSTable__tbody__tr__td"
                                    id={`capex-placeholder-period-${index}`}
                                >
                                    <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                        <UncontrolledTooltip target={`capex-placeholder-period-${index}`}>
                                            {CASH_FLOW_TOOLTIP_MESSAGE}
                                        </UncontrolledTooltip>
                                    </RenderIf>
                                </td>
                            ))}
                        </tr>
                    </RenderIf>
                </tbody>
                <tfoot className="NSTable__tfoot">
                    {table.getFooterGroups().map(footerGroup => (
                        <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                            {footerGroup.headers.map(header => (
                                <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                    {flexRender(
                                        header.column.columnDef.footer,
                                        header.getContext(),
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </tfoot>
            </NSTable>

            <LeasingCostReservesModal
                isOpen={isLeasingCostReservesModalOpen}
                toggle={() => setIsLeasingCostReservesModalOpen(false)}
            />
        </>
    );
};

export default CapitalExpendituresTable;
